import React, { useState, useEffect } from 'react';
import { adminAPI, unwrapList } from '../services/api';
import Pagination, { paginateItems } from './Pagination';

function SystemSettings() {
  const [specializations, setSpecializations] = useState([]);
  const [newSpecialization, setNewSpecialization] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSpecId, setSelectedSpecId] = useState(null);
  const [editingSpec, setEditingSpec] = useState({ id: null, name: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch specializations when component mounts
  useEffect(() => {
    fetchSpecializations();
  }, []);

  const fetchSpecializations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getSpecializations();
      const specs = unwrapList(response.data).map(spec => ({
        id: spec.id,
        name: spec.name,
        created_at: spec.created_at
      }));
      setSpecializations(specs);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') return;
      setError('Failed to fetch specializations');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSpecialization = async () => {
    if (newSpecialization.trim() === '') return;
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminAPI.addSpecialization({
        name: newSpecialization.trim()
      });
      
      setSpecializations([...specializations, {
        id: response.data.id,
        name: response.data.name,
        created_at: response.data.created_at
      }]);
      
      setNewSpecialization('');
      setShowAddModal(false);
    } catch (err) {
      setError('Failed to add specialization');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (id) => {
    const spec = specializations.find(s => s.id === id);
    setEditingSpec({...spec});
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (editingSpec.name.trim() === '') return;
    
    setIsLoading(true);
    try {
      await adminAPI.updateSpecialization(editingSpec.id, {
        name: editingSpec.name.trim()
      });
      
      setSpecializations(specializations.map(spec => 
        spec.id === editingSpec.id ? {...editingSpec, name: editingSpec.name.trim()} : spec
      ));
      
      setShowEditModal(false);
    } catch (err) {
      setError('Failed to update specialization');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSpecialization = async (id) => {
    setIsLoading(true);
    try {
      await adminAPI.deleteSpecialization(id);
      setSpecializations(specializations.filter(spec => spec.id !== id));
      setShowConfirmModal(false);
    } catch (err) {
      setError('Failed to delete specialization');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const { pageItems, total, currentPage: safePage } = paginateItems(
    unwrapList(specializations),
    currentPage
  );

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-semibold mb-6">System Settings</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-medium">Specializations</h2>
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            disabled={isLoading}
          >
            Add Specialization
          </button>
        </div>
        
        {isLoading && <p className="text-gray-500">Loading...</p>}
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageItems.length > 0 ? (
                pageItems.map(spec => (
                <tr key={spec.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{spec.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditClick(spec.id)}
                        className="px-3 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                        disabled={isLoading}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedSpecId(spec.id);
                          setShowConfirmModal(true);
                        }}
                        className="px-3 py-1 rounded text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200"
                        disabled={isLoading}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                    No specializations found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={safePage} total={total} onPageChange={setCurrentPage} />
      
      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Add Specialization</h2>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                Name
              </label>
              <input
                id="name"
                type="text"
                value={newSpecialization}
                onChange={(e) => setNewSpecialization(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter specialization name"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleAddSpecialization}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                disabled={!newSpecialization.trim() || isLoading}
              >
                {isLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Edit Specialization</h2>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="edit-name">
                Name
              </label>
              <input
                id="edit-name"
                type="text"
                value={editingSpec.name}
                onChange={(e) => setEditingSpec({...editingSpec, name: e.target.value})}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                disabled={!editingSpec.name.trim() || isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
            <p className="mb-6 text-gray-700">
              Are you sure you want to delete this specialization? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                onClick={() => handleDeleteSpecialization(selectedSpecId)}
                className="px-4 py-2 text-white rounded-md text-sm font-medium bg-red-600 hover:bg-red-700"
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemSettings;









